import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import { CustomButton, CustomDatePicker } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../../services/Api";

const StudyMaterialsList = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};

  const user = TokenService.getUser();
  const userId = user?.id || "";
  const clientData = user?.client_data?.[0] || {};
  const schoolId =
    clientData.school_id != null && clientData.school_id !== ""
      ? Number(clientData.school_id)
      : null;
  const academic_id = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [columnFilters, setColumnFilters] = useState([]);

  // ==================== DELETE FEATURE STATE ====================
  const [selectedId, setSelectedId] = useState(null);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
  });

  const handleColumnFiltersChange = (filters) => {
    setColumnFilters(filters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const getFilterValue = (id) => {
    const f = columnFilters.find((c) => c.id === id);
    return f?.value || "";
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        user_id: userId,
        school_id: schoolId,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        title: getFilterValue("title"),
        topic: getFilterValue("topic"),
        sub_topic: getFilterValue("sub_topic"),
        subject: getFilterValue("subject"),
        class_id: getFilterValue("class"),
        section_id: getFilterValue("section_id"),
        medium: getFilterValue("medium"),
        student_name: getFilterValue("student_name"),
        created_date: getFilterValue("created_at"),
        search: "",
        academic_id: academic_id,
      };

      const res = await AxiosInstance.post(
        "/book/study-material/list",
        payload,
      );
      if (res?.data?.status) {
        setData(res.data.data || []);
        setRowCount(res.data.pagination?.total || 0);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error("Study material list error:", err);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, columnFilters, userId, schoolId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Clear the row selection whenever the dataset changes (page/filter change)
  // so a stale selected id from another page does not stay highlighted.
  useEffect(() => {
    setSelectedId(null);
  }, [data]);

  const handleView = (row) => {
    navigate("/user/ViewStudyMaterial", {
      state: {
        ID: row.assessment_id,
        data: row,
      },
    });
  };

  const formatDate = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };

  // ==================== DELETE HANDLERS ====================
  // Step 1: user clicks DELETE -> open confirmation popup
  const handleDeleteClick = () => {
    if (!selectedId) return;
    setModalState({
      open: true,
      type: "Confirm",
      message: t?.confirmDelete || "Are you sure you want to delete?",
    });
  };

  // Step 2: user clicks OK in the confirmation popup -> hit the delete API
  const handleConfirmDelete = async () => {
    try {
      const res = await AxiosInstance.post("/book/deleteStudyMaterial", {
        id: selectedId,
        user_id: userId,
      });

      if (res?.data?.status === true) {
        setModalState({
          open: true,
          type: "success",
          message:
            res?.data?.message || t?.deleteSuccess || "Successfully deleted",
        });
        setSelectedId(null);
        fetchList();
      } else {
        setModalState({
          open: true,
          type: "failed",
          message: res?.data?.message || t?.deleteFailed || "Failed to delete",
        });
      }
    } catch (err) {
      console.error("Delete study material error:", err);
      setModalState({
        open: true,
        type: "failed",
        message: t?.deleteFailed || "Failed to delete",
      });
    }
  };

  const handleModalClose = () => {
    setModalState({ open: false, type: "", message: "" });
  };

  // ==================== SINGLE DATE FILTER ====================
  const SingleDateFilter = ({ column }) => {
    const filterValue = column.getFilterValue();
    const [localDate, setLocalDate] = useState(filterValue || "");

    // Keep local state in sync if the external filter is cleared from outside
    useEffect(() => {
      setLocalDate(filterValue || "");
    }, [filterValue]);

    const dateValue = localDate ? dayjs(localDate) : null;

    const handleChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalDate(formatted);
      // Pushes the value into MRT's columnFilters via handleColumnFiltersChange
      column.setFilterValue(formatted || undefined);
    };

    return (
      <CustomDatePicker
        name={`${column.id}_date`}
        value={dateValue}
        onChange={handleChange}
        placeholder={t?.createdDate || "Created Date"}
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
      />
    );
  };

  const columns = useMemo(
    () => [
      // ==================== SELECT CHECKBOX COLUMN ====================
      {
        accessorKey: "select",
        header: "",
        enableColumnFilter: false,
        enableSorting: false,
        size: 50,
        Cell: ({ row }) => {
          const rowId = row.original.id;
          const isChecked = selectedId === rowId;
          return (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() =>
                setSelectedId((prev) => (prev === rowId ? null : rowId))
              }
              className={styles.radioInput}
              aria-label="Select row"
            />
          );
        },
      },
      { accessorKey: "title", header: t.title || "Title" },
      { accessorKey: "topic", header: t.topic || "Topic" },
      {
        accessorKey: "sub_topic",
        header: t.subTopic || "Sub Topic",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      { accessorKey: "subject", header: t.subject || "Subject" },
      { accessorKey: "class", header: t.class || "Class" },
      {
        accessorKey: "section_id",
        header: t.section || "Section",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      { accessorKey: "medium", header: t.medium || "Medium" },
      {
        accessorKey: "student_name",
        header: t.studentName || "Student Name",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "created_at",
        header: t.createdDate || "Created Date",
        Filter: ({ column }) => <SingleDateFilter column={column} />,
        Cell: ({ cell }) => formatDate(cell.getValue()),
        size: 200,
      },
      {
        accessorKey: "actions",
        header: t.action || "Action",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <button
            type="button"
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={t.viewStudyMaterial || "View Study Material"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [t, selectedId],
  );

  return (
    <div>
      {/* DELETE-only action row (no ADD button for study materials) */}
      <div className={styles.actionsRow}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.deleteButton}
          fullWidth={false}
          onClick={handleDeleteClick}
          disabled={!selectedId}
        >
          {t.delete || "DELETE"}
        </CustomButton>
      </div>

      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        hideGlobalSearch={true}
        // Tell MRT to identify rows by their unique `id` so its internal
        // row-id never collides between rows that share an assessment_id.
        getRowId={(originalRow) => String(originalRow.id)}
      />

      {/* ==================== POPUP MODAL ====================
       */}
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? translations?.labels?.success || t?.success || "Success"
            : modalState.type === "Confirm"
              ? translations?.labels?.delete || t?.delete || "DELETE"
              : translations?.labels?.failed || t?.failed || "Failed"
        }
        message={modalState.message}
        onClose={handleModalClose}
        onOk={modalState.type === "Confirm" ? handleConfirmDelete : undefined}
      />
    </div>
  );
};

export default StudyMaterialsList;
